import { Button, Chip, makeStyles } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ReplayIcon from '@material-ui/icons/Replay';
import { ActivityStatus } from '../../api/types';

type ViewMode = 'reading' | 'chat';

export interface ControlBarProps {
  activityStatus: ActivityStatus;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  canUseViewModes: boolean;
  disableReading?: boolean;
  showApproveButton: boolean;
  approveButtonLabel: string;
  approving: boolean;
  disableApprove?: boolean;
  isChatStreaming: boolean;
  isTaskStreaming: boolean;
  onApprove: () => void;
  showRestartButton?: boolean;
  restartButtonLabel?: string;
  onRestart?: () => void;
  disableRestart?: boolean;
}

const STATUS_CHIP_LABELS: Record<ActivityStatus, string> = {
  preparing: 'Preparando',
  active: 'Em execu\u00e7\u00e3o',
  completed: 'Conclu\u00edda',
  error: 'Erro',
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.5, 1),
    marginBottom: theme.spacing(0.5),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
      gap: theme.spacing(0.5),
      justifyContent: 'center',
    },
  },
  left: {
    display: 'flex',
    alignItems: 'center',
  },
  center: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
      justifyContent: 'center',
    },
  },
  right: {
    display: 'flex',
    alignItems: 'center',
  },
  statusChip: {
    fontWeight: 600,
    fontSize: '0.68rem',
    height: 22,
  },
  statusActive: {
    borderColor: theme.palette.secondary.main,
    color: theme.palette.secondary.main,
  },
  statusCompleted: {
    borderColor: theme.palette.success?.main || '#4caf50',
    color: theme.palette.success?.main || '#4caf50',
  },
  statusError: {
    borderColor: theme.palette.error.main,
    color: theme.palette.error.main,
  },
  statusPreparing: {
    borderColor: theme.palette.grey[500],
    color: theme.palette.grey[500],
  },
  viewButton: {
    minWidth: 70,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.72rem',
    padding: theme.spacing(0.4, 1.5),
  },
  activeViewButton: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.secondary.dark,
    },
  },
  approveButton: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.72rem',
    padding: theme.spacing(0.4, 1.5),
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.secondary.dark,
    },
  },
}));

function getStatusChipClass(
  status: ActivityStatus,
  classes: ReturnType<typeof useStyles>,
): string {
  switch (status) {
    case 'active':
      return classes.statusActive;
    case 'completed':
      return classes.statusCompleted;
    case 'error':
      return classes.statusError;
    default:
      return classes.statusPreparing;
  }
}

export const ControlBar = ({
  activityStatus,
  viewMode,
  onViewModeChange,
  canUseViewModes,
  disableReading = false,
  showApproveButton,
  approveButtonLabel,
  approving,
  disableApprove = false,
  isChatStreaming,
  isTaskStreaming,
  onApprove,
  showRestartButton = false,
  restartButtonLabel = 'Reiniciar',
  onRestart,
  disableRestart = false,
}: ControlBarProps) => {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <div className={classes.left}>
        <Chip
          label={STATUS_CHIP_LABELS[activityStatus]}
          variant="outlined"
          size="small"
          className={`${classes.statusChip} ${getStatusChipClass(
            activityStatus,
            classes,
          )}`}
        />
      </div>

      <div className={classes.center}>
        {canUseViewModes && (
          <>
            <Button
              variant={viewMode === 'reading' ? 'contained' : 'outlined'}
              size="small"
              className={`${classes.viewButton} ${
                viewMode === 'reading' ? classes.activeViewButton : ''
              }`}
              onClick={() => onViewModeChange('reading')}
              disabled={disableReading}
              aria-label="Modo leitura"
            >
              Leitura
            </Button>
            <Button
              variant={viewMode === 'chat' ? 'contained' : 'outlined'}
              size="small"
              className={`${classes.viewButton} ${
                viewMode === 'chat' ? classes.activeViewButton : ''
              }`}
              onClick={() => onViewModeChange('chat')}
              aria-label="Modo chat"
            >
              Chat
            </Button>
          </>
        )}
        {showRestartButton && onRestart && (
          <Button
            variant="outlined"
            size="small"
            className={classes.viewButton}
            startIcon={<ReplayIcon style={{ fontSize: 14 }} />}
            onClick={onRestart}
            disabled={disableRestart}
          >
            {restartButtonLabel}
          </Button>
        )}
      </div>

      <div className={classes.right}>
        {showApproveButton && (
          <Button
            variant="contained"
            size="small"
            className={classes.approveButton}
            startIcon={<CheckCircleIcon />}
            onClick={onApprove}
            disabled={
              approving || disableApprove || isChatStreaming || isTaskStreaming
            }
          >
            {approveButtonLabel}
          </Button>
        )}
      </div>
    </div>
  );
};
